import React, { useState } from 'react';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const LogCard = ({ data, special, onCommentSubmit, isSubmitting }) => {
  const userId = Cookies.get('userId');
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [commentText, setCommentText] = useState('');

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    if (isNaN(date)) return '---';

    const day = date.getDate();
    const month = date.toLocaleString('en-GB', { month: 'short' });

    const getSuffix = (d) => {
      if (d > 3 && d < 21) return 'th';
      switch (d % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    };

    return `${day}${getSuffix(day)} ${month}`;
  };

  const formattedDate = formatDate(data?.updatedAt);

  const handleCommentSubmit = async () => {
    if (!commentText.trim()) return;

    try {
      await onCommentSubmit(data.id, commentText);
      setShowCommentInput(false);
      setCommentText('');
    } catch (error) {
      console.error('Error handling comment submission:', error);
    }
  };

  // Check if comments exist (handles both array and string cases)
  const hasComments = () => {
    if (Array.isArray(data.comment)) {
      return data.comment.length > 0;
    }
    return !!data.comment; // handles string, null, undefined cases
  };

  const getInitials = (name) => {
    if (!name) return '';
    const names = name.split(' ');
    return names
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  return (
    <div className="relative flex w-full">
      {/* Left timeline with dashed line */}
      <div className="flex flex-col items-center w-[60px] relative">
        {/* Date chip above the dot */}
        <div className="absolute top-[-1.2rem] left-1/2 transform -translate-x-1/2">
          <div className="text-[9px] bg-[#E9F5F6] px-2 py-0.5 rounded-full text-gray-700 font-semibold shadow-sm">
            {formattedDate}
          </div>
        </div>

        {/* Dashed vertical line */}
        <div className="h-full w-[1px] border-l border-dashed border-gray-400"></div>
      </div>

      {/* Right content */}
      <div className="flex-1 flex flex-col gap-2 pb-6">
        <div className="bg-[#E9F5F6] p-4 rounded-md shadow-sm w-full">
          {/* Title */}
          <div className="font-semibold text-gray-800 text-base mb-3">
            {data?.description || data?.name || 'Activity Title'}
          </div>

          {/* Metadata section below description */}
          <div className="flex items-center gap-3 text-xs text-gray-500 justify-end">
            {/* Created by info */}
            {data?.createdBy && (
              <div className="flex items-center gap-1.5 bg-white px-2 py-1 rounded-full border border-gray-200">
                <svg
                  className="w-3 h-3 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                <span className="text-gray-600">By</span>
                <span className="font-medium text-gray-700">{data.createdBy.name}</span>
              </div>
            )}

            {/* Time info */}
            <div className="flex items-center gap-1.5 bg-white px-2 py-1 rounded-full border border-gray-200">
              <svg
                className="w-3 h-3 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-gray-600">At</span>
              <span className="font-medium text-gray-700">
                {new Date(data.createdAt).toLocaleTimeString('en-IN', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Existing comment - updated condition */}
        {data.comment?.length > 0 && (
          <div className="space-y-3 ml-2">
            {data.comment.map((commentObj, idx) => (
              <div key={idx} className="flex gap-3 items-start">
                {/* User avatar with initials */}
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium">
                    {getInitials(commentObj.user?.name)}
                  </div>
                </div>

                {/* Comment content */}

                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-gray-800">
                    {commentObj.user?.name || 'Unknown User'}
                  </span>
                </div>
                <p className=" text-gray-700">{commentObj.comment}</p>
              </div>
            ))}
          </div>
        )}

        {/* Comment input (shown when clicking "Comment") */}
        {showCommentInput && (
          <div className="flex gap-2 items-start ml-2 mt-2">
            <div className="flex-1 flex flex-col">
              <input
                type="text"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Type your comment..."
                className="text-sm p-2 border border-gray-300 rounded-md w-full"
                onKeyDown={(e) => e.key === 'Enter' && handleCommentSubmit()}
              />
              <div className="flex justify-end gap-2 mt-2">
                <button
                  onClick={() => setShowCommentInput(false)}
                  className="text-xs px-3 py-1 text-gray-600 hover:bg-gray-100 rounded">
                  Cancel
                </button>
                <button
                  onClick={handleCommentSubmit}
                  className="text-xs px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                  disabled={!commentText.trim() || isSubmitting}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Comment link - updated condition */}
        {!hasComments() && !showCommentInput && (
          <div
            className="text-sm text-blue-500 hover:underline mt-1 cursor-pointer ml-2"
            onClick={() => setShowCommentInput(true)}>
            Comment
          </div>
        )}
      </div>
    </div>
  );
};

export default LogCard;
